import { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Trash2, Copy, MoreHorizontal, CalendarClock, Info } from 'lucide-react';
import {
  Alert, Button, Badge, Checkbox, ConfirmDialog, EmptyState, FilterChip, IconButton, Menu, Select, ToggleGroup,
} from '@parsina/framework';
import { useTranslation } from '../../../i18n';
import { C } from '../../../constants/theme';
import { STACK } from '../../../constants/density.js';
import TimePicker from '../../../components/common/TimePicker.jsx';
import {
  PRESENCE_DAYS, PRESENCE_ISSUE, presenceIssues, nextPresenceRange, copyPresenceDay,
  splitPresenceRange, presenceReadiness, normalizeServiceIds,
} from '../personnel.js';

/**
 * ساعات حضور — the weekly shifts a PERFORMER works at THIS clinic: a
 * practitioner, and since PM-0468 §4.2 a staff member who performs a service.
 * Layout and behaviour: `docs/parsina_clinic_hours_wireframe.md`, service
 * windows per `docs/SERVICE_OFFERING_AND_BOOKING_DESIGN.md` §2.3 / §4.2.
 *
 * This is the heart of the person↔clinic relationship: it is what makes them
 * bookable, and `scheduling` enforces it at booking time. The rows live in
 * `scheduling.doctor_presences`, keyed on the provider; the clinic writes the
 * whole week atomically through `replaceProviderPresences` (a merge would
 * resurrect a shift the manager just deleted).
 *
 * Each range may be narrowed to SERVICES (`serviceCatalogIds`, empty = every
 * service the person performs — the 90 %). «۸–۱۰ خدمت A، ۱۰–۱۲ خدمت B» is two
 * ranges with two lists; the picker offers only what this person performs, so
 * a window can never name a service they do not do. «تقسیم بازه» makes that
 * pair in one click.
 *
 * `value` stays the flat wire list — the drawer's dirty flag and the mutation
 * both read it — and the editor only GROUPS it: one row per weekday, 0 = شنبه,
 * Saturday-first (DS §6.3). Within a day, ranges keep their insertion order
 * rather than being sorted, so a row never jumps away from under the cursor
 * while its start time is being typed.
 *
 * «فعال» is derived from the data (a day with ranges is on) plus one piece of
 * view state: a day switched on, or whose last range was just deleted, stays
 * on with «بدون شیفت» until the manager turns it off. That state is reset with
 * `resetKey`.
 *
 * Validation is inline (`role="alert"`) and non-blocking here: the error sits
 * under the range it is about and the save is refused by the caller. Two
 * touching ranges with different lists are fine (filter-then-merge).
 *
 * Props:
 *   services        — [{ catalogId, name }] this person performs; null = not known
 *   servicesLoading — the list is still being read
 *   persona         — 'practitioner' | 'staff' (the empty state's wording)
 */
/** One-tap boundaries in the picker — where a clinic shift nearly always starts or ends. */
const SHIFT_TIMES = ['07:00', '08:00', '10:00', '12:00', '14:00', '16:00', '18:00', '20:00'];

const toMin = (hhmm) => { const [h, m] = String(hhmm || '').split(':').map(Number); return h * 60 + m; };
const toHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
/** The midpoint of a range, rounded down to five minutes — the split dialog's proposal. */
const midpoint = (s) => {
  const a = toMin(s.startTime); const b = toMin(s.endTime);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return s.startTime;
  return toHHMM(Math.floor((a + b) / 2 / 5) * 5);
};

export default function PresenceEditor({
  value = [], onChange, disabled = false, resetKey,
  services = null, servicesLoading = false, persona = 'practitioner',
}) {
  const { t, weekdayNames, formatList, formatNumber } = useTranslation();
  const names = weekdayNames();

  const [openDays, setOpenDays] = useState(() => new Set());
  useEffect(() => { setOpenDays(new Set()); }, [resetKey]);

  const [copy, setCopy] = useState(null);           // { from, locked }
  const [disabling, setDisabling] = useState(null); // day index awaiting confirmation
  const [splitting, setSplitting] = useState(null); // slot index awaiting a cut time
  const [removing, setRemoving] = useState(null);   // slot index awaiting a Delete confirm

  const performedIds = Array.isArray(services) ? services.map((s) => String(s.catalogId)) : null;
  const issues = useMemo(() => presenceIssues(value, { performedIds }), [value, performedIds]);
  const byDay = useMemo(() => PRESENCE_DAYS.map((d) => value
    .map((slot, index) => ({ ...slot, index }))
    .filter((s) => Number(s.dayOfWeek) === d)), [value]);
  const daysWithHours = PRESENCE_DAYS.filter((d) => byDay[d].length > 0);
  const uncovered = useMemo(() => presenceReadiness(value, services), [value, services]);
  const serviceName = (id) => services?.find((s) => String(s.catalogId) === String(id))?.name || String(id);

  const setOpen = (day, on) => setOpenDays((prev) => {
    const next = new Set(prev);
    if (on) next.add(day); else next.delete(day);
    return next;
  });

  const update = (i, patch) => onChange(value.map((slot, idx) => (idx === i ? { ...slot, ...patch } : slot)));
  const addRange = (day) => { setOpen(day, true); onChange([...value, nextPresenceRange(value, day)]); };
  const removeRange = (day, i) => { setOpen(day, true); onChange(value.filter((_, idx) => idx !== i)); };
  const clearDay = (day) => { setOpen(day, false); onChange(value.filter((s) => Number(s.dayOfWeek) !== day)); };

  const toggleDay = (day, on) => {
    if (on) { addRange(day); return; }
    if (byDay[day].length > 0) setDisabling(day); else setOpen(day, false);
  };

  const emptyText = persona === 'staff' && services?.length
    ? t('people.presence.staffEmpty', { count: services.length, list: formatList(services.map((s) => s.name)) })
    : t('people.presence.emptyBlocksBooking');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.tight }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 700, color: C.textPrimary }}>{t('people.presence.title')}</h3>
        {!disabled && (
          <Button
            variant="outline"
            size="sm"
            disabled={daysWithHours.length === 0}
            onClick={() => setCopy({ from: daysWithHours[0], locked: false })}
          >
            <Copy size={14} /> {t('people.presence.copyAll')}
          </Button>
        )}
      </div>

      {/* Zone ②: the readiness strip — which services sit in NO range. Found
          once, not one item at a time (UX #21). Informational, never an error. */}
      {uncovered.length > 0 && (
        <Alert variant="info" icon={<Info size={16} />} data-testid="people-schedule-readiness">
          <div>{t('people.presence.readiness', { count: uncovered.length, list: formatList(uncovered.map((s) => s.name)) })}</div>
          <div style={{ fontSize: '0.71875rem', color: C.textMuted, marginTop: 2 }}>{t('people.presence.readinessHint')}</div>
        </Alert>
      )}

      {value.length === 0 && (
        persona === 'staff' ? (
          <div data-testid="people-schedule-empty-staff">
            <EmptyState
              icon={<CalendarClock size={44} strokeWidth={1.5} aria-hidden />}
              title={emptyText}
              description={t('people.presence.staffDeskNote')}
            />
          </div>
        ) : (
          <div style={{
            fontSize: '0.78125rem', color: C.textMuted, lineHeight: 1.9,
            border: `1px dashed ${C.border}`, borderRadius: 10, padding: '10px 12px',
          }}>
            {emptyText}
          </div>
        )
      )}

      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, background: C.card }}>
        {PRESENCE_DAYS.map((day) => (
          <DayRow
            key={day}
            day={day}
            name={names[day]}
            ranges={byDay[day]}
            issues={issues}
            enabled={byDay[day].length > 0 || openDays.has(day)}
            disabled={disabled}
            last={day === PRESENCE_DAYS.length - 1}
            services={services}
            servicesLoading={servicesLoading}
            serviceName={serviceName}
            onToggle={(on) => toggleDay(day, on)}
            onAdd={() => addRange(day)}
            onRemove={(i) => removeRange(day, i)}
            onAskRemove={(i) => setRemoving(i)}
            onSplit={(i) => setSplitting(i)}
            onCopy={() => setCopy({ from: day, locked: true })}
            onUpdate={update}
          />
        ))}
      </div>

      {copy && (
        <CopyDialog
          names={names}
          from={copy.from}
          locked={copy.locked}
          sources={daysWithHours}
          byDay={byDay}
          onClose={() => setCopy(null)}
          onApply={(from, targets, mode) => {
            onChange(copyPresenceDay(value, from, targets, mode));
            setOpenDays((prev) => new Set([...prev, ...targets]));
            setCopy(null);
          }}
        />
      )}

      {splitting != null && value[splitting] && (
        <SplitDialog
          slot={value[splitting]}
          dayName={names[Number(value[splitting].dayOfWeek)]}
          onClose={() => setSplitting(null)}
          onSplit={(at) => {
            const next = splitPresenceRange(value, splitting, at);
            if (next) onChange(next);
            setSplitting(null);
          }}
        />
      )}

      <ConfirmDialog
        open={removing != null}
        onOpenChange={(open) => { if (!open) setRemoving(null); }}
        title={removing != null && value[removing]
          ? t('people.presence.deleteRange.title', {
            day: names[Number(value[removing].dayOfWeek)],
            start: value[removing].startTime, end: value[removing].endTime,
          })
          : ''}
        confirmLabel={t('people.presence.deleteRange.confirm')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={() => {
          if (removing != null && value[removing]) removeRange(Number(value[removing].dayOfWeek), removing);
          setRemoving(null);
        }}
      />

      <ConfirmDialog
        open={disabling != null}
        onOpenChange={(open) => { if (!open) setDisabling(null); }}
        title={disabling != null ? t('people.presence.disable.title', { day: names[disabling] }) : ''}
        description={t('people.presence.disable.body')}
        confirmLabel={t('people.presence.disable.confirm')}
        cancelLabel={t('common.actions.cancel')}
        destructive
        onConfirm={() => { clearDay(disabling); setDisabling(null); }}
      />
    </div>
  );
}

function DayRow({
  day, name, ranges, issues, enabled, disabled, last, services, servicesLoading, serviceName,
  onToggle, onAdd, onRemove, onAskRemove, onSplit, onCopy, onUpdate,
}) {
  const { t } = useTranslation();
  const count = ranges.length;

  return (
    <div
      role="group"
      aria-label={name}
      data-testid={`people-schedule-day-${day}`}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 12px',
        borderBottom: last ? 'none' : `1px solid ${C.border}`,
        background: enabled ? 'transparent' : C.bg,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 40 }}>
        <span style={{
          minWidth: 64, fontSize: '0.84375rem', fontWeight: 600,
          color: enabled ? C.textPrimary : C.textMuted,
        }}>
          {name}
        </span>
        <DaySwitch
          checked={enabled}
          disabled={disabled}
          label={t('people.presence.toggleDay', { day: name })}
          onChange={onToggle}
        />
        <span style={{ fontSize: '0.75rem', color: enabled ? C.textSecondary : C.textMuted }}>
          {enabled ? t('people.presence.enabled') : t('people.presence.disabled')}
        </span>
        <Badge variant="outline">{t('people.presence.shiftCount', { count })}</Badge>
        <span style={{ flex: 1 }} />
        {!disabled && count > 0 && (
          <IconButton
            variant="ghost"
            size={40}
            icon={<Copy size={15} />}
            label={t('people.presence.copyDay', { day: name })}
            onClick={onCopy}
          />
        )}
      </div>

      {enabled && ranges.map((slot, n) => (
        <RangeRow
          key={slot.index}
          day={day}
          n={n}
          name={name}
          slot={slot}
          issue={issues[slot.index]}
          disabled={disabled}
          services={services}
          servicesLoading={servicesLoading}
          serviceName={serviceName}
          onUpdate={onUpdate}
          onRemove={() => onRemove(slot.index)}
          onAskRemove={() => onAskRemove(slot.index)}
          onSplit={() => onSplit(slot.index)}
        />
      ))}

      {enabled && !disabled && (
        <div>
          <Button variant="ghost" size="sm" onClick={onAdd} aria-label={t('people.presence.addRangeFor', { day: name })}>
            <Plus size={14} /> {t('people.presence.addRange')}
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * One range: the two times, its «خدمات» chip set, the `⋯` menu and the
 * delete. The row is a focusable group so `Delete` on it asks before removing
 * (never while the caret is in one of its time inputs).
 */
function RangeRow({
  day, n, name, slot, issue, disabled, services, servicesLoading, serviceName,
  onUpdate, onRemove, onAskRemove, onSplit,
}) {
  const { t } = useTranslation();
  const errorId = issue ? `presence-err-${slot.index}` : undefined;
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const id = `people-schedule-range-${day}-${n}`;
  const issueText = issue === PRESENCE_ISSUE.ORDER
    ? t('people.presence.errorOrder')
    : issue === PRESENCE_ISSUE.OVERLAP
      ? t('people.presence.errorOverlap')
      : issue === PRESENCE_ISSUE.UNKNOWN_SERVICE
        ? t('people.presence.errorUnknownService')
        : '';
  const blocking = issue === PRESENCE_ISSUE.ORDER || issue === PRESENCE_ISSUE.OVERLAP;

  return (
    <div
      data-testid={id}
      role="group"
      tabIndex={disabled ? -1 : 0}
      aria-label={t('people.presence.rangeLabel', { n: n + 1, day: name, start: slot.startTime, end: slot.endTime })}
      onKeyDown={(e) => {
        if (disabled || e.key !== 'Delete' || e.target !== e.currentTarget) return;
        e.preventDefault();
        onAskRemove();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 4, borderRadius: 8, outlineOffset: 2 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <TimePicker
          suggestions={SHIFT_TIMES}
          value={slot.startTime}
          disabled={disabled}
          invalid={blocking}
          describedBy={errorId}
          label={t('people.presence.startOf', { day: name, n: n + 1 })}
          onChange={(v) => onUpdate(slot.index, { startTime: v })}
        />
        <span aria-hidden style={{ color: C.textMuted }}>—</span>
        <TimePicker
          suggestions={SHIFT_TIMES}
          value={slot.endTime}
          disabled={disabled}
          invalid={blocking}
          describedBy={errorId}
          label={t('people.presence.endOf', { day: name, n: n + 1 })}
          onChange={(v) => onUpdate(slot.index, { endTime: v })}
        />

        <ServiceChips
          testid={`${id}-services`}
          slot={slot}
          dayName={name}
          disabled={disabled}
          services={services}
          servicesLoading={servicesLoading}
          serviceName={serviceName}
          onChange={(ids) => onUpdate(slot.index, { serviceCatalogIds: ids })}
        />

        <span style={{ flex: 1 }} />
        {!disabled && (
          <>
            {/* The kit IconButton does not forward a ref (CL §5 gap) — the anchor is its wrapper. */}
            <span ref={menuRef} style={{ display: 'inline-flex' }}>
              <IconButton
                variant="ghost"
                size={40}
                icon={<MoreHorizontal size={15} />}
                label={t('people.presence.menu.label', { day: name, start: slot.startTime, end: slot.endTime })}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
                data-testid={`${id}-menu`}
              />
            </span>
            <Menu
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              anchorRef={menuRef}
              width={220}
              ariaLabel={t('people.presence.menu.label', { day: name, start: slot.startTime, end: slot.endTime })}
              data-testid={`${id}-menu-popover`}
            >
              <button type="button" role="menuitem" style={MENU_ITEM} onClick={() => { setMenuOpen(false); onSplit(); }}>
                {t('people.presence.split.action')}
              </button>
              <button type="button" role="menuitem" style={{ ...MENU_ITEM, color: C.red }} onClick={() => { setMenuOpen(false); onAskRemove(); }}>
                {t('people.presence.removeRange', { day: name, start: slot.startTime, end: slot.endTime })}
              </button>
            </Menu>
            <IconButton
              variant="ghost"
              size={40}
              icon={<Trash2 size={15} />}
              label={t('people.presence.removeRange', { day: name, start: slot.startTime, end: slot.endTime })}
              onClick={onRemove}
            />
          </>
        )}
      </div>
      {issue && (
        <div id={errorId} role="alert" style={{ fontSize: '0.75rem', color: blocking ? C.red : CT_WARN }}>
          {issueText}
        </div>
      )}
    </div>
  );
}

const CT_WARN = '#92400e';
const MENU_ITEM = {
  display: 'block', width: '100%', textAlign: 'start', padding: '8px 10px', border: 'none',
  background: 'transparent', fontFamily: 'inherit', fontSize: '0.78125rem', cursor: 'pointer', borderRadius: 6,
  color: C.textPrimary,
};

/**
 * «خدمات» on a range — a `FilterChip` that reads the list («همهٔ خدمات» / the
 * one name / two names / «۳ خدمت») and opens an inline popover listing ONLY
 * the services this person performs. The popover is a roving group with
 * `aria-pressed` buttons; Escape closes it.
 */
function ServiceChips({ testid, slot, dayName, disabled, services, servicesLoading, serviceName, onChange }) {
  const { t, formatNumber } = useTranslation();
  const chipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const ids = normalizeServiceIds(slot.serviceCatalogIds);

  const label = ids.length === 0
    ? t('people.presence.services.all')
    : ids.length <= 2
      ? ids.map(serviceName).join(t('common.punctuation.listSeparator'))
      : t('people.presence.services.count', { count: ids.length });

  const toggle = (catalogId) => {
    const key = String(catalogId);
    onChange(ids.includes(key) ? ids.filter((x) => x !== key) : [...ids, key]);
  };

  const move = (e, i, n) => {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const buttons = e.currentTarget.parentElement.querySelectorAll('button[aria-pressed]');
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowDown' ? 1 : n - 1)) % n;
    buttons[next]?.focus();
  };

  return (
    <span data-testid={testid} data-state={ids.length ? 'narrowed' : 'all'} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: '0.6875rem', color: C.textMuted }}>{t('people.presence.services.label')}:</span>
      <FilterChip
        ref={chipRef}
        label={label}
        active={ids.length > 0}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ minHeight: 32 }}
      />
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={chipRef}
        width={260}
        ariaLabel={t('people.presence.services.pick', { day: dayName, start: slot.startTime, end: slot.endTime })}
        data-testid={`${testid}-popover`}
      >
        <div
          role="group"
          aria-label={t('people.presence.services.pick', { day: dayName, start: slot.startTime, end: slot.endTime })}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); chipRef.current?.focus?.(); } }}
          style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 4 }}
        >
          <div style={{ fontSize: '0.6875rem', color: C.textMuted, padding: '2px 6px 6px', lineHeight: 1.7 }}>
            {t('people.presence.services.hint')}
          </div>
          {servicesLoading && !services ? (
            <div style={{ fontSize: '0.75rem', color: C.textMuted, padding: '4px 6px' }}>{t('people.presence.services.loading')}</div>
          ) : !services?.length ? (
            <div style={{ fontSize: '0.75rem', color: C.textMuted, padding: '4px 6px' }}>{t('people.presence.services.none')}</div>
          ) : services.map((svc, i) => {
            const on = ids.includes(String(svc.catalogId));
            return (
              <button
                key={svc.catalogId}
                type="button"
                aria-pressed={on}
                aria-label={svc.name}
                data-menu-focus={i === 0 ? 'true' : undefined}
                onClick={() => toggle(svc.catalogId)}
                onKeyDown={(e) => move(e, i, services.length)}
                style={{
                  ...MENU_ITEM,
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: on ? C.navyLight : 'transparent', fontWeight: on ? 700 : 500,
                }}
              >
                <span aria-hidden style={{
                  width: 14, height: 14, borderRadius: 4, border: `1.5px solid ${on ? C.navy : C.border}`,
                  background: on ? C.navy : '#fff', flexShrink: 0,
                }} />
                {svc.name}
                {svc.durationMinutes ? (
                  <span style={{ marginInlineStart: 'auto', fontSize: '0.6875rem', color: C.textMuted }}>
                    {formatNumber(svc.durationMinutes)}′
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </Menu>
    </span>
  );
}

/** «تقسیم بازه» — one time, two rows; both inherit the list. */
function SplitDialog({ slot, dayName, onClose, onSplit }) {
  const { t } = useTranslation();
  const [at, setAt] = useState(() => midpoint(slot));
  const valid = !!at && slot.startTime < at && at < slot.endTime;
  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={t('people.presence.split.title', { day: dayName, start: slot.startTime, end: slot.endTime })}
      description={t('people.presence.split.body')}
      confirmLabel={t('people.presence.split.confirm')}
      cancelLabel={t('common.actions.cancel')}
      confirmDisabled={!valid}
      onConfirm={() => valid && onSplit(at)}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <TimePicker
          suggestions={SHIFT_TIMES.filter((x) => slot.startTime < x && x < slot.endTime)}
          value={at}
          invalid={!valid}
          label={t('people.presence.split.at')}
          onChange={setAt}
        />
        {!valid && <div role="alert" style={{ fontSize: '0.75rem', color: C.red }}>{t('people.presence.split.invalid')}</div>}
      </div>
    </ConfirmDialog>
  );
}

/**
 * «کپی ساعات» — one dialog for both doors. From a day's own copy button the
 * source is fixed; from the header the manager picks it. Targets default to
 * every other day, and «جایگزین کردن» is the default mode because «همه روزها
 * مثل شنبه» is what the button is pressed for — the dialog itself is the
 * confirmation, and it names the consequence when a target already has hours.
 */
function CopyDialog({ names, from: initialFrom, locked, sources, byDay, onClose, onApply }) {
  const { t } = useTranslation();
  const [from, setFrom] = useState(initialFrom);
  const [targets, setTargets] = useState(() => PRESENCE_DAYS.filter((d) => d !== initialFrom));
  const [mode, setMode] = useState('replace');

  const chosen = targets.filter((d) => d !== from);
  const overwrites = mode === 'replace' && chosen.some((d) => byDay[d].length > 0);

  return (
    <ConfirmDialog
      open
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={locked ? t('people.presence.copy.titleDay', { day: names[from] }) : t('people.presence.copy.title')}
      confirmLabel={t('common.actions.apply')}
      cancelLabel={t('common.actions.cancel')}
      confirmDisabled={chosen.length === 0}
      onConfirm={() => onApply(from, chosen, mode)}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: STACK.regular }}>
        {!locked && (
          <Select
            label={t('people.presence.copy.source')}
            placeholder={t('common.form.chooseEllipsis')}
            value={String(from)}
            onChange={(e) => setFrom(Number(e.target.value))}
            options={sources.map((d) => ({ value: String(d), label: names[d] }))}
          />
        )}
        <fieldset style={{ border: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <legend style={{ fontSize: '0.78125rem', color: C.textSecondary, marginBottom: 6 }}>
            {t('people.presence.copy.targets')}
          </legend>
          {PRESENCE_DAYS.filter((d) => d !== from).map((d) => (
            <Checkbox
              key={d}
              label={names[d]}
              checked={targets.includes(d)}
              onChange={(on) => setTargets((prev) => (on ? [...prev, d] : prev.filter((x) => x !== d)))}
            />
          ))}
        </fieldset>
        <ToggleGroup
          label={t('people.presence.copy.mode')}
          value={mode}
          onChange={(v) => { if (v) setMode(v); }}
          options={[
            { value: 'replace', label: t('people.presence.copy.replace') },
            { value: 'append', label: t('people.presence.copy.append') },
          ]}
        />
        {overwrites && (
          <div style={{ fontSize: '0.75rem', color: C.textSecondary, lineHeight: 1.8 }}>
            {t('people.presence.copy.overwriteWarning')}
          </div>
        )}
      </div>
    </ConfirmDialog>
  );
}

/**
 * The day's on/off control. The kit `Switch` is a `<div>` no keyboard can
 * reach and `Toggle` brings a full labelled row with its own divider, so this
 * is the kit's switch geometry on a real `<button role="switch">` — Tab, Space
 * and Enter work, and `aria-checked` rather than colour carries the state.
 */
function DaySwitch({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 999, padding: 0, flexShrink: 0, position: 'relative',
        border: 'none', background: checked ? C.navy : '#d1d5db',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
        transition: 'background .2s',
      }}
    >
      <span style={{
        position: 'absolute', top: 3, width: 16, height: 16, borderRadius: '50%',
        background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        right: checked ? 3 : 21, transition: 'right .2s',
      }} />
    </button>
  );
}
